/**
 * Single source of truth for the clinician + patient dashboard navigation.
 *
 * Why this module exists (Audit Issue 15, 16, 17):
 *   - Issue 15: Every CaraLoom internal page should ship the same sidebar.
 *     Before this module each page hand-rolled its own header or copy-pasted
 *     the navItems list, so adding a route forced 7+ edits and pages
 *     occasionally went out-of-sync.
 *   - Issue 16: Several entries used bare `#` hrefs that produced
 *     dead-clicks when the user wasn't already on the dashboard. Anchor
 *     hrefs are now expressed as absolute paths (`/clinician/dashboard#…`)
 *     so they always work, no matter where the user is.
 *   - Issue 17: The four overlapping clinician inboxes (Care Requests,
 *     Case Requests, Offers, Engagements) are consolidated into a single
 *     `/clinician/inbox` entry. The standalone pages still exist for
 *     deep-link compatibility, but the menu now points at one place.
 *
 * Usage:
 *   import { clinicianNavItems, patientNavItems } from '../components/brand/navItems';
 *   const navItems = clinicianNavItems({ pathname, pendingMatchesCount: 3 });
 *   return <DashboardShell navItems={navItems} ...>...</DashboardShell>;
 */
import {
  Activity, Users, BookOpen, Megaphone, BookmarkCheck, Heart, Landmark, Calendar,
  Compass, MessageSquare, Handshake, CreditCard, FileText, ShieldCheck, FolderOpen,
  Inbox, Mic, Brain, Store, DollarSign, Wallet, Building2, Sparkles, IdCard, Share2,
  Stethoscope, ClipboardList, AlertTriangle, BarChart3, ShieldAlert,
} from 'lucide-react';
import { isFeatureLive } from '../../lib/caraloom_v2_ia';

const isActive = (href, pathname) => {
  if (!href || !pathname) return false;
  // Anchor hrefs (e.g. `/patient/dashboard#insurance`) match the base path,
  // but only when the hash isn't on a non-dashboard page.
  const [basePath] = href.split('#');
  if (!basePath) return false;
  if (basePath === pathname) return true;
  // Exact prefix match for nested routes so e.g. /clinician/inbox?tab=offers
  // still highlights "Inbox".
  return pathname === basePath;
};

/**
 * Drop nav items whose `featureId` is gated off by the current CaraLoom
 * tier (caraloom_v2_ia.js → FEATURE_LIFECYCLE). Items without a featureId
 * always render. Exported so per-page nav arrays can use the same filter.
 */
export const filterLiveNav = (items = []) =>
  items.filter((it) => !it.featureId || isFeatureLive(it.featureId));

export function patientNavItems({ pathname = '' } = {}) {
  return filterLiveNav([
    { icon: Sparkles, label: 'Find Care', href: '/patient/find-care', group: 'Find Care', highlight: true },
    { icon: Activity, label: 'Dashboard', href: '/patient/dashboard', group: 'Overview' },
    // Find Care surfaces
    { icon: Users, label: 'Find Clinicians', href: '/patient/find-clinicians', group: 'Find Care' },
    { icon: BookOpen, label: 'Browse Marketplace', href: '/marketplace', group: 'Find Care',
      featureId: 'care.public-marketplace' },
    { icon: Megaphone, label: 'Post a Need', href: '/patient/care-requests', group: 'Find Care',
      featureId: 'care.care-requests-lynk' },
    { icon: BookmarkCheck, label: 'Saved Searches', href: '/patient/saved-searches', group: 'Find Care',
      featureId: 'care.saved-searches' },
    { icon: Heart, label: 'Saved Clinicians', href: '/patient/saved-clinicians', group: 'Find Care',
      featureId: 'care.saved-clinicians' },
    { icon: Landmark, label: 'Benefits & Funding', href: '/patient/benefits', group: 'Find Care' },
    // My Care Journey
    { icon: Calendar, label: 'Appointments', href: '/patient/dashboard#appointments', group: 'My Care Journey' },
    { icon: Heart, label: 'Daily Check-in', href: '/patient/checkin', group: 'My Care Journey' },
    { icon: Compass, label: 'Care Navigator', href: '/patient/navigator', group: 'My Care Journey',
      featureId: 'care.ai-care-navigator' },
    // Activity & Network
    { icon: MessageSquare, label: 'Messages', href: '/patient/messages', group: 'Activity & Network' },
    { icon: Handshake, label: 'Offers & Bookings', href: '/patient/offers', group: 'Activity & Network' },
    { icon: Users, label: 'Family Portal', href: '/patient/family', group: 'Activity & Network' },
    { icon: Share2, label: 'Family Access & ACP', href: '/family/share', group: 'Activity & Network',
      hint: 'Grant/revoke status + advance-care-planning access' },
    // Records & Billing
    { icon: CreditCard, label: 'Insurance', href: '/patient/dashboard#insurance', group: 'Records & Billing' },
    { icon: CreditCard, label: 'Billing & Statements', href: '/patient/billing', group: 'Records & Billing' },
    { icon: FileText, label: 'Documents', href: '/patient/documents', group: 'Records & Billing' },
    // System
    { icon: ShieldCheck, label: 'Privacy & Data', href: '/privacy/dashboard', group: 'System' },
  ]).map((it) => ({ ...it, active: it.active ?? isActive(it.href, pathname) }));
}

export function plannerNavItems({ pathname = '' } = {}) {
  // The discharge-planner sidebar surfaces only the spine workflow. The
  // older legacy "/planner/dashboard" lives outside this builder.
  return filterLiveNav([
    { icon: Inbox,        label: 'Referrals',         href: '/spine/discharge-planner', group: 'Spine',
      highlight: true, hint: 'Intake · matching · placements' },
    { icon: BarChart3,    label: 'Hospital scoreboard', href: '/spine/scoreboard',      group: 'Spine',
      hint: 'Latency · readmit rate · $ protected' },
    { icon: ShieldCheck,  label: 'Privacy & Data',    href: '/privacy/dashboard',       group: 'System' },
  ]).map((it) => ({ ...it, active: it.active ?? isActive(it.href, pathname) }));
}


export function clinicianNavItems({ pathname = '', pendingMatchesCount = 0 } = {}) {
  return filterLiveNav([
    { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard', group: 'Overview' },
    // Opportunities — incoming work, consolidated (Issue 17)
    { icon: Inbox, label: 'Inbox', href: '/clinician/inbox', group: 'Opportunities',
      highlight: true, badge: pendingMatchesCount || null,
      hint: 'Care Requests · Case Requests · Offers · Engagements' },
    // Spine offers + readmission-watch tasks — surfaced inline so clinicians
    // can jump straight into the discharge-orchestration workflow.
    { icon: Handshake, label: 'Spine offers', href: '/spine/clinician', group: 'Opportunities',
      hint: 'First-to-accept wins · auto-cascade on decline' },
    { icon: ShieldAlert, label: 'Watch tasks', href: '/spine/clinician/watches', group: 'Opportunities',
      hint: '30-day readmission touchpoints' },
    { icon: Megaphone, label: 'Care Requests', href: '/clinician/care-requests', group: 'Opportunities',
      featureId: 'care.care-requests-lynk' },
    // Care Delivery
    { icon: Calendar, label: 'Schedule', href: '/clinician/schedule', group: 'Care Delivery' },
    { icon: Heart, label: 'Advance Care Planning', href: '/planner/acp', group: 'Care Delivery',
      hint: 'Goals of care · directives (read/log)' },
    { icon: FileText, label: 'Clinical Notes', href: '/clinician/notes', group: 'Care Delivery' },
    { icon: Mic, label: 'Voice Notes', href: '/clinician/voice-notes', group: 'Care Delivery' },
    { icon: Brain, label: 'Risk Monitor', href: '/clinician/risk-monitor', group: 'Care Delivery' },
    { icon: MessageSquare, label: 'Messages', href: '/clinician/messages', group: 'Care Delivery' },
    // Practice & Profile
    { icon: Store, label: 'My Practice', href: '/clinician/practice', group: 'Practice & Profile', highlight: true },
    { icon: Compass, label: 'Practice Coach', href: '/clinician/coach', group: 'Practice & Profile',
      featureId: 'workforce.ai-practice-coach' },
    { icon: IdCard, label: 'Practice Profile', href: '/clinician/practice-profile', group: 'Practice & Profile' },
    { icon: ClipboardList, label: 'Credentialing', href: '/clinician/credentialing', group: 'Practice & Profile' },
    { icon: Share2, label: 'Public Profile', href: '/clinician/public-profile', group: 'Practice & Profile' },
    // Finance & Admin — Phase 7 slim: banking/payouts/billing panels are
    // v2-tier featureIds, so they disappear at the v1 tier (code preserved).
    { icon: DollarSign, label: 'Billing & RCM', href: '/clinician/billing', group: 'Finance & Admin',
      featureId: 'rcm.claim-submit' },
    { icon: ShieldCheck, label: 'Denial Prediction', href: '/clinician/denial-prediction', group: 'Finance & Admin',
      featureId: 'rcm.claim-submit' },
    { icon: Activity, label: 'Executive Dashboard', href: '/operations/executive', group: 'Finance & Admin',
      featureId: 'rcm.claim-submit' },
    { icon: Wallet, label: 'Earnings & Payouts', href: '/clinician/payouts', group: 'Finance & Admin',
      featureId: 'workforce.payouts' },
    { icon: Landmark, label: 'Banking & Benefits', href: '/clinician/banking', group: 'Finance & Admin',
      featureId: 'workforce.banking' },
    { icon: ShieldCheck, label: 'Insurance', href: '/clinician/insurance', group: 'Finance & Admin' },
    { icon: FolderOpen, label: 'Documents', href: '/clinician/documents', group: 'Finance & Admin' },
    // System
    { icon: ShieldCheck, label: 'Privacy & Data', href: '/privacy/dashboard', group: 'System' },
  ]).map((it) => ({ ...it, active: it.active ?? isActive(it.href, pathname) }));
}

// Re-export the shared icons so consuming pages don't need to import lucide directly.
export const NavIcons = {
  Activity, Users, BookOpen, Megaphone, BookmarkCheck, Heart, Landmark, Calendar,
  Compass, MessageSquare, Handshake, CreditCard, FileText, ShieldCheck, FolderOpen,
  Inbox, Mic, Brain, Store, DollarSign, Wallet, Building2, Sparkles, IdCard, Share2,
  Stethoscope, ClipboardList,
};
